/**
 * Accueil (home) configurable components (Phase 3):
 * - `extranet.homeQuickAccess` — the counter tiles (`QuickAccessTiles`),
 *   stable tile keys `contrats` / `sinistres` / `primes` / `vehicules`,
 *   section keys `accesRapide` / `flotte`;
 * - `extranet.pendingPiecesBanner` — the « pièces en attente » banner
 *   (`PendingPiecesBanner`), item `description`, action `viewList`.
 *
 * Each registration goes through `registerOnce` (idempotent) with the base
 * rule-context schema, and the module exports the `withComponentRules(…)`
 * wrappers the Accueil page imports.
 */

import { z } from 'zod';

import { withComponentRules } from '@components/shared/configurable';

import { extranetBaseContextSchema } from '../../contextVariables';
import { EXTRANET_MODULE, registerOnce } from '../registerHelpers';
import { PendingPiecesBanner, type PendingPiecesBannerProps } from './PendingPiecesBanner';
import { PENDING_PIECES_BANNER_ID } from './pendingPiecesBannerConfig';
import { HOME_QUICK_ACCESS_ID } from './quickAccessConfig';
import { QuickAccessTiles, type QuickAccessTilesProps } from './QuickAccessTiles';

export { HOME_QUICK_ACCESS_ID } from './quickAccessConfig';
export { PENDING_PIECES_BANNER_ID } from './pendingPiecesBannerConfig';

// ---------------------------------------------------------------------------
// Props schemas (data props only — `_layoutOverrides` is an injected meta prop)
// ---------------------------------------------------------------------------

/** Props of `extranet.homeQuickAccess` tracked by the registry. */
export type HomeQuickAccessRegistrationProps = Pick<QuickAccessTilesProps, 'counts'>;

const countSchema = z.number().int().nonnegative().optional();

export const homeQuickAccessPropsSchema = z.object({
  counts: z
    .object({
      contrats: countSchema,
      sinistres: countSchema,
      primes: countSchema,
      vehicules: countSchema,
    })
    .optional(),
}) satisfies z.ZodType<HomeQuickAccessRegistrationProps>;

/** Props of `extranet.pendingPiecesBanner` tracked by the registry. */
export type PendingPiecesBannerRegistrationProps = Pick<PendingPiecesBannerProps, 'count'>;

export const pendingPiecesBannerPropsSchema = z.object({
  count: countSchema,
}) satisfies z.ZodType<PendingPiecesBannerRegistrationProps>;

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/** Register the Accueil components. Idempotent. */
export function registerHomeComponents(): void {
  registerOnce<HomeQuickAccessRegistrationProps>({
    id: HOME_QUICK_ACCESS_ID,
    displayName: "Accès rapide de l'accueil",
    module: EXTRANET_MODULE,
    component: QuickAccessTiles,
    propsSchema: homeQuickAccessPropsSchema,
    defaultPropLabels: {
      counts: 'Compteurs imposés (aperçu)',
    },
    previewProps: {
      counts: { contrats: 8, sinistres: 6, primes: 8, vehicules: 11 },
    },
    contextVariables: extranetBaseContextSchema,
  });

  registerOnce<PendingPiecesBannerRegistrationProps>({
    id: PENDING_PIECES_BANNER_ID,
    displayName: 'Bandeau des pièces en attente',
    module: EXTRANET_MODULE,
    component: PendingPiecesBanner,
    propsSchema: pendingPiecesBannerPropsSchema,
    defaultPropLabels: {
      count: 'Nombre de pièces imposé (aperçu)',
    },
    previewProps: { count: 5 },
    contextVariables: extranetBaseContextSchema,
  });
}

// Eager registration on import (idempotent — HMR / test re-imports are safe).
registerHomeComponents();

/** Rule-aware tiles — the Accueil page imports this as `QuickAccessTiles`. */
export const QuickAccessTilesConfigurable = withComponentRules(
  QuickAccessTiles,
  HOME_QUICK_ACCESS_ID,
);

/** Rule-aware banner — the Accueil page imports this as `PendingPiecesBanner`. */
export const PendingPiecesBannerConfigurable = withComponentRules(
  PendingPiecesBanner,
  PENDING_PIECES_BANNER_ID,
);
